/* ============================================================================
 * Sconto Scatola — Data layer
 * ============================================================================
 * Modello dati e funzioni di utilità per il sistema "Sconto Scatola".
 *
 * Sorgente di verità: il **prodotto stesso** (campi `boxQty` / `boxPrice` /
 * `boxDemo` provenienti da MongoDB e configurabili dal pannello admin).
 *
 * Per retro-compatibilità con i componenti che accedono per chiave SKU,
 * espone un registry runtime ``PRODUCT_BOX_DISCOUNTS`` che viene popolato
 * automaticamente quando un prodotto arriva dal backend
 * (`registerBoxDiscount` invocato da productService.normalizeProduct).
 *
 * Funzionalità ERP: NON gestita. Solo configurazione client-side e via Mongo.
 * ============================================================================ */

export type BoxDiscount = { boxQty: number; boxPrice: number; demo?: boolean };

// Registry runtime — mutato dal lato React all'arrivo dei prodotti
export const PRODUCT_BOX_DISCOUNTS: Record<string, BoxDiscount> = {};

/**
 * Registra/aggiorna lo sconto scatola per uno SKU. Se i parametri sono
 * mancanti/zero, lo SKU viene rimosso dal registry. Chiamato sia da
 * `productService.normalizeProduct` (cataloghi) sia da `productService.get`
 * (dettaglio prodotto). Idempotente.
 */
export function registerBoxDiscount(
  sku: string,
  boxQty?: number | null,
  boxPrice?: number | null,
  demo?: boolean | null,
): void {
  if (!sku) return;
  if (boxQty && boxPrice && boxQty > 0 && boxPrice > 0) {
    PRODUCT_BOX_DISCOUNTS[sku] = { boxQty, boxPrice, demo: !!demo };
  } else {
    delete PRODUCT_BOX_DISCOUNTS[sku];
  }
}

/**
 * calcola prezzo con sconto scatola.
 * Output:
 *  - boxes: numero di scatole intere
 *  - remainder: pezzi sciolti residui (qty % boxQty)
 *  - totalPrice: prezzo finale (scatole + sfusi)
 *  - savings: risparmio rispetto al prezzo singolo per tutta la qty
 */
export function calcBoxPrice(
  sku: string,
  qty: number,
  unitPrice: number,
): { boxes: number; remainder: number; totalPrice: number; savings: number } {
  const box = PRODUCT_BOX_DISCOUNTS[sku];
  return calcBoxPriceFor(box?.boxQty, box?.boxPrice, qty, unitPrice);
}

/**
 * Variante "pura" di `calcBoxPrice` che non dipende dal registry runtime
 * `PRODUCT_BOX_DISCOUNTS` (popolato solo come side-effect della navigazione
 * su liste/dettaglio prodotto — può essere vuoto per articoli già nel
 * carrello ma non ancora "visti" nella sessione corrente, es. dopo un
 * refresh con carrello ripristinato da MongoDB). Usa direttamente i campi
 * `boxQty`/`boxPrice` già presenti sul CartItem (spread da Product al
 * momento dell'aggiunta, vedi CartContext.addItem) — robusta e sempre
 * corretta indipendentemente dalla cronologia di navigazione. Bug fix
 * 13/07/2026 (il riepilogo sconto scatola nel drawer del carrello non si
 * mostrava se l'articolo non era stato ri-visitato nella sessione).
 */
export function calcBoxPriceFor(
  boxQty: number | null | undefined,
  boxPrice: number | null | undefined,
  qty: number,
  unitPrice: number,
): { boxes: number; remainder: number; totalPrice: number; savings: number } {
  if (!boxQty || !boxPrice || boxQty <= 0 || boxPrice <= 0 || qty < boxQty) {
    return { boxes: 0, remainder: qty, totalPrice: qty * unitPrice, savings: 0 };
  }
  const boxes = Math.floor(qty / boxQty);
  const remainder = qty % boxQty;
  const totalPrice = (boxes * boxPrice) + (remainder * unitPrice);
  const normalPrice = qty * unitPrice;
  return { boxes, remainder, totalPrice, savings: normalPrice - totalPrice };
}
